import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  StyleSheet,
  View,
  Modal,
  Text,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import ModalHeader from '../../../common/components/basicmodal/ModalHeader';
import { commonModalStyle } from '../../../common/components/basicmodal';
import { MapPosition } from '../../../common/constants/AppConstants';
import { Map, AdvancedMarker, Pin, InfoWindow, useMapsLibrary, useMap, useApiLoadingStatus, APILoadingStatus } from '@vis.gl/react-google-maps';
import { GOOGLE_MAP_Id } from '../../../../env';

interface MapModalProps {
  isVisible: boolean;
  onClose: () => void;
  onLocationSelect: (location: {latitude: number, longitude: number}) => void;
  initialLatitude?: number | null;
  initialLongitude?: number | null;
  initialLocationName?: string | null;
}

const MapModal = ({
  isVisible,
  onClose,
  onLocationSelect,
  initialLatitude,
  initialLongitude,
  initialLocationName,
}: MapModalProps) => {
  const [selectedLocation, setSelectedLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initialCenterSet, setInitialCenterSet] = useState(false);

  // For search input
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [locationDetails, setLocationDetails] = useState<{name: string, lat: number, lng: number} | null>(null);
  const [mapsReady, setMapsReady] = useState(false);

  // Google Maps API loading status
  const apiStatus = Platform.OS === 'web' ? useApiLoadingStatus() : null;
  const isApiAuthFailure = apiStatus === APILoadingStatus.AUTH_FAILURE || apiStatus === APILoadingStatus.FAILED;

  // Google Places search using PlacesService
  const map = Platform.OS === 'web' ? useMap() : null;
  const placesLib = Platform.OS === 'web' ? useMapsLibrary('places') : null;
  const geocoderLib = Platform.OS === 'web' ? useMapsLibrary('geocoding') : null;

  // Set mapsReady when both map and placesLib are available
  useEffect(() => {
    if (map && placesLib) {
      setMapsReady(true);
    } else {
      setMapsReady(false);
    }
  }, [map, placesLib]);

  const placesSearch = useCallback((query: string) => {
    setSearchError(null);
    setSearchLoading(true);

    // If we already have coordinates selected, don't show an error
    // if the place search fails (we'll keep using the coordinates)
    const hasExistingCoordinates = !!selectedLocation;

    if (!placesLib || !map) {
      if (!hasExistingCoordinates) {
        setSearchError('Map or Places library not loaded. Please try again.');
      }
      setSearchLoading(false);
      return;
    }

    const service = new placesLib.PlacesService(map);
    service.findPlaceFromQuery(
      {
        query,
        fields: ['geometry', 'name'],
      },
      (results: any, status: string) => {
        if (status === 'OK' && results && results.length > 0) {
          const loc = results[0].geometry.location;
          setSelectedLocation({ latitude: loc.lat(), longitude: loc.lng() });
          setLocationDetails({
            name: results[0]?.name || "",
            lat: loc.lat(),
            lng: loc.lng(),
          });
          // Center and zoom the map
          if (map) {
            map.setCenter(loc);
            map.setZoom(16); // Adjust zoom level as needed
          }
          // Clear any error that might have been shown
          setSearchError(null);
        } else {
          // Only show error if we don't have coordinates already
          if (!hasExistingCoordinates) {
            setSearchError('Location not found. Please try another search.');
          }
        }
        setSearchLoading(false);
      }
    );
  }, [map, placesLib, selectedLocation]);

  const handleSearch = () => {
    if (!searchText.trim()) {
      setSearchError('Please enter a location.');
      return;
    }
    placesSearch(searchText.trim());
  };

  // Effect for modal visibility and initial setup
  useEffect(() => {
    if (isVisible) {
      // Reset error states
      setSearchError(null);
      setSearchLoading(false);
      
      // Set initial search text if provided
      if (initialLocationName && initialLocationName.trim() !== '') {
        setSearchText(initialLocationName);
      } else {
        setSearchText('');
      }
      
      // Reset initialization flag when modal is opened
      setInitialCenterSet(false);
      
      setIsLoading(true); // Set loading true initially when modal opens
      // Delay to ensure the map is potentially re-centered or ready
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    } else {
      // When modal is closed, reset all relevant states to ensure clean slate for next open
      setSelectedLocation(null);
      setLocationDetails(null);
      setSearchText('');
      setSearchError(null);
      setSearchLoading(false);
      setIsLoading(true); // Reset loading state, map will be hidden
      setInitialCenterSet(false); // Reset initialization flag
    }
  }, [isVisible, initialLocationName]);

  // Separate effect to handle initialLocationName search - ONLY RUN ONCE when modal opens
  useEffect(() => {
    // Only proceed if modal is visible, maps are ready, and we haven't set initial center yet
    if (isVisible && mapsReady && !initialCenterSet) {
      setInitialCenterSet(true); // Mark as initialized to prevent repeated center resets
      
      // If we have coordinates, create location details without a search
      if (initialLatitude && initialLongitude) {
        const lat = parseFloat(initialLatitude.toString());
        const lng = parseFloat(initialLongitude.toString());
        
        setSelectedLocation({
          latitude: lat,
          longitude: lng
        });
        
        // Set location details with coordinates
        setLocationDetails({
          name: initialLocationName || "",
          lat: lat,
          lng: lng
        });
        
        // Center the map on these coordinates ONLY ONCE when opening
        if (map) {
          map.setCenter({ lat, lng });
          map.setZoom(15);
        }
      } 
      // Only perform a search if we don't have coordinates but have a location name
      else if (initialLocationName && initialLocationName.trim() !== '') {
        // Small delay to ensure map is fully loaded
        const timer = setTimeout(() => {
          placesSearch(initialLocationName.trim());
        }, 600);
        
        return () => clearTimeout(timer);
      }
    }
  }, [isVisible, mapsReady, initialLatitude, initialLongitude, initialLocationName, placesSearch, map]);

  const reverseGeocode = useCallback((lat: number, lng: number) => {
    if (!geocoderLib) return;
  
    const geocoder = new geocoderLib.Geocoder();
  
    geocoder.geocode({ location: { lat, lng } }, (results: any, status: string) => {
      if (status === 'OK' && results && results.length > 0) {
        setSearchText(results[0].formatted_address); // Update the input text
      } else {
        console.warn('Geocoder failed due to:', status);
      }
    });
  }, [geocoderLib]);

  useEffect(() => {
    if (selectedLocation) {
      reverseGeocode(selectedLocation.latitude, selectedLocation.longitude);
    }
  }, [selectedLocation, reverseGeocode]);  

  const handleMapClick = useCallback((event: any) => {
    // Handle map click to set location
    if (event && event.detail && event.detail.latLng) {
      // For @vis.gl/react-google-maps, latLng is an object with lat() and lng() methods
      let lat, lng;
      
      try {
        // First try to use the methods (Google Maps standard)
        if (typeof event.detail.latLng.lat === 'function' && typeof event.detail.latLng.lng === 'function') {
          lat = event.detail.latLng.lat();
          lng = event.detail.latLng.lng();
        } 
        // If not methods, try to access as properties
        else if (typeof event.detail.latLng.lat === 'number' && typeof event.detail.latLng.lng === 'number') {
          lat = event.detail.latLng.lat;
          lng = event.detail.latLng.lng;
        }
        // If latLng is just an object with numbers
        else if (!isNaN(parseFloat(event.detail.latLng.lat)) && !isNaN(parseFloat(event.detail.latLng.lng))) {
          lat = parseFloat(event.detail.latLng.lat);
          lng = parseFloat(event.detail.latLng.lng);
        }
        
        // If we have valid coordinates, update the state
        if (lat !== undefined && lng !== undefined) {
          console.log(`Map clicked: ${lat}, ${lng}`);
          setSelectedLocation({
            latitude: lat,
            longitude: lng,
          });
          setLocationDetails({
            name: "",
            lat: lat,
            lng: lng,
          });
          // Clear any error messages since we have a valid location now
          setSearchError(null);
        }
      } catch (err) {
        console.error('Error handling map click:', err);
      }
    }
  }, []);

  const handleConfirm = () => {
    if (selectedLocation) {
      onLocationSelect({
        latitude: selectedLocation.latitude, longitude: selectedLocation.longitude
      });
      onClose();
    }
  };

  const mapElement = useMemo(() => {
    // Determine the center coordinates for INITIAL render only
    let centerLat = MapPosition.lat;
    let centerLng = MapPosition.lng;
    
    if (initialLatitude && initialLongitude) {
      centerLat = parseFloat(initialLatitude.toString());
      centerLng = parseFloat(initialLongitude.toString());
    }

    // Show error UI when API auth fails (quota exceeded, key issue, etc.)
    if (isApiAuthFailure) {
      return (
        <div style={{
          width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa',
          padding: 32,
        }}>
          <MaterialIcons name="error-outline" size={64} color="#dc3545" />
          <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#333', marginTop: 16, textAlign: 'center' }}>
            Google Maps failed to load
          </Text>
          <Text style={{ fontSize: 14, color: '#666', marginTop: 8, textAlign: 'center', maxWidth: 400 }}>
            This is usually a temporary issue. Please reload the page to try again.
            If the problem persists, contact support.
          </Text>
          <TouchableOpacity
            onPress={() => { if (Platform.OS === 'web') window.location.reload(); }}
            style={{
              marginTop: 20, paddingHorizontal: 24, paddingVertical: 12,
              backgroundColor: '#007bff', borderRadius: 8,
            }}
          >
            <Text style={{ color: '#fff', fontSize: 16, fontWeight: '600' }}>Reload Page</Text>
          </TouchableOpacity>
        </div>
      );
    }
    
    return (
      <div style={{ width: '100%', height: '100%' }}>
        {Platform.OS === 'web' && (
          <Map
            defaultCenter={{ lat: centerLat, lng: centerLng }}
            defaultZoom={13.5}
            gestureHandling={'greedy'}
            disableDefaultUI={true}
            mapId={GOOGLE_MAP_Id}
            onClick={handleMapClick}
          >
            {selectedLocation && (
              <AdvancedMarker
                position={{ lat: selectedLocation.latitude, lng: selectedLocation.longitude }}
              >
                <Pin
                  background="#33adff"
                  borderColor="green"
                  glyphColor="white"
                />
              </AdvancedMarker>
            )}
          </Map>
        )}
      </div>
    );
  }, [initialLatitude, initialLongitude, handleMapClick, selectedLocation, isApiAuthFailure]);

  return (
    <Modal animationType="slide" visible={isVisible} transparent={true} style={styles.fullScreenModal}>
      <View style={styles.fullScreenContainer}>
        <ModalHeader label="Select Location" closeModal={onClose} />

        <View style={styles.mapContainer}>
          {isLoading && (
            <View style={styles.overlay}>
              <ActivityIndicator size="large" color="#0000ff" />
            </View>
          )}
          {mapElement}
          
          {/* Floating Search Input and Location Details */}
          {Platform.OS === 'web' && (
            <View style={styles.floatingSearchWrapper}>
              <View style={styles.floatingSearchRow}>
                <View style={[styles.searchInputContainer, !selectedLocation && { flex: 1 }]}>
                  <TextInput
                    style={styles.floatingSearchInput}
                    value={searchText}
                    onChangeText={setSearchText}
                    placeholder="Search location..."
                    placeholderTextColor="#aaa"
                    onSubmitEditing={handleSearch}
                    editable={!searchLoading}
                  />
                  <TouchableOpacity onPress={handleSearch} disabled={searchLoading} style={styles.searchButton}>
                    <MaterialIcons name="search" size={24} color="#fff" />
                  </TouchableOpacity>
                  {searchLoading && <ActivityIndicator size="small" color="#fff" style={{ marginLeft: 4 }} />}
                </View>
                
                {selectedLocation && (
                  <View style={styles.locationDetailsContainer}>
                    <MaterialIcons name="location-on" size={20} color="#007bff" style={styles.locationIcon} />
                    <Text style={styles.locationDetailsText} numberOfLines={1} ellipsizeMode="tail">
                     Lat: {locationDetails?.lat?.toFixed(6) || selectedLocation.latitude.toFixed(6)}{" "}
                     Long: {locationDetails?.lng?.toFixed(6) || selectedLocation.longitude.toFixed(6)}
                    </Text>
                  </View>
                )}
              </View>
              {searchError && !selectedLocation && (
                <Text style={styles.errorText}>{searchError}</Text>
              )}
              {!mapsReady && isVisible && !isApiAuthFailure && (
                <Text style={styles.loadingText}>Loading maps, please wait...</Text>
              )}
            </View>
          )}
        </View>
        <View style={styles.footerContainer}>
          <TouchableOpacity onPress={handleConfirm} disabled={!selectedLocation} style={[
            styles.confirmButton,
            !selectedLocation && styles.disabledButton
          ]}>
            <Text style={styles.addButton}>Confirm Location</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  ...commonModalStyle,
  floatingSearchWrapper: {
    position: 'absolute',
    top: 10,
    left: 0,
    right: 0,
    zIndex: 10,
    maxWidth: "50%",
    marginHorizontal: 'auto',
  },
  floatingSearchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 8,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 0.7, // Take up 70% of the row
  },
  locationDetailsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 0.3, // Take up 30% of the row
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    padding: 6,
    marginLeft: 8,
  },
  locationIcon: {
    marginRight: 4,
  },
  locationDetailsText: {
    fontSize: 14,
    color: '#333',
    flex: 1,
  },
  searchButton: {
    padding: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(0, 123, 255, 0.7)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  floatingSearchInput: {
    height: 40,
    borderWidth: 0,
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    padding: 8,
    fontSize: 16,
    flex: 1,
    color: '#555',
    backgroundColor: 'transparent',
  },
  errorText: {
    color: '#ff6666',
    textAlign: 'center',
    marginTop: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    padding: 4,
    borderRadius: 4,
  },
  loadingText: {
    color: '#ffffff',
    textAlign: 'center',
    marginTop: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    padding: 4,
    borderRadius: 4,
  },
  fullScreenModal: {
    flex: 1,
    margin: 0,
    padding: 0,
  },
  fullScreenContainer: {
    flex: 1,
    backgroundColor: 'white',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  mapContainer: {
    flex: 1,
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  footerContainer: {
    padding: 15,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    backgroundColor: 'white',
    alignItems: 'center',
  },
  confirmButton: {
    width: '100%',
    alignItems: 'center',
  },
  disabledButton: {
    opacity: 0.5,
  },
  overlay: {
    ...commonModalStyle.overlay,
    zIndex: 1000,
  }
});

export default MapModal;